import { useEffect, useState } from 'react';
import { CollapseSection } from '../CollapseSection';
import { IconButton } from '@/components/ats';
import { FaPlus } from 'react-icons/fa';
import { Trash01 } from '@kula-ai/icons/outline/Trash01';
import { handleLinkedInUrlPaste } from '@/utils/linkedinUrlHelpers';
import * as Yup from 'yup';
import { useField } from 'formik';
import { Avatar, Box, Divider, Flex, Icon, Link, Text } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { getCountryFromTimezone } from '@/utils/countryUtils';
import { useParams } from 'react-router-dom';
import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';
import { MarkAsReferredField } from '@/components/contacts/candidate-job-drawer/tabs/current-job/candidate-info/sections/MarkAsReferredField';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { components } from 'react-select';
import { getFirstApplicationReviewStage } from '@/lib/util';
import { generateCustomFormConfig } from '@/formConfigs/generateCustomFormConfig';
import moment from 'moment';
import { CONTACT_SOCIALS } from './constants';
import { UnDeleteCandidateBanner } from '../actions/delete';
import { useQuery } from '@tanstack/react-query';
import { TimeZoneSelectorV1 } from '@/components/formBuilder/formFields/DateField/components/TimeZoneSelectorV1';
import {
  REGEX_PATTERNS,
  CREATE_CONTACT_JOB_KEY_REGEX,
} from '@/constants/regex';
import { useGetAllJobsListQuery } from '@/store/query/ats-jobs';
import { JobListMenu } from '../filters/components/UiItems/JobList';
import { ATS_MILESTONES } from '@/ui-library/components/constants';
import { StageAutomationPreview } from '@/components/automation/MoveWithAutomationModal';
import { renderStageOptionLabel } from '@/components/automation/renderStageOptionLabel';
import {
  checkContactMatching,
  hasValidLinkedInUrl,
} from './CreateContactHelpers';
import { isValidEmail } from '@/constants/regex';
import {
  SelectOptionWithAvatar,
  SelectWithDescriptionOption,
} from '@/components/formBuilder/SelectComponents';
import { getCandidateSourceComponents } from '@/components/formBuilder/SelectComponents/CandidateSourceSelect';

function extractSocials(obj) {
  const kinds = [];

  // Safety check for obj parameter
  if (!obj || typeof obj !== 'object') {
    return kinds;
  }

  // Iterate over all keys in the object
  Object.keys(obj).forEach((key) => {
    const kindMatch = key.match(/^socials_kind_(\d+)$/);

    // Check if the key matches socials_kind_* pattern
    if (kindMatch) {
      const kind = obj[key];
      if (kind) {
        kinds.push(kind);
      }
    }
  });

  return kinds;
}

function extractJobIds(obj) {
  const jobIds = [];

  if (!obj || typeof obj !== 'object') {
    return jobIds;
  }

  Object.keys(obj).forEach((key) => {
    if (CREATE_CONTACT_JOB_KEY_REGEX.test(key) && obj[key]) {
      jobIds.push(obj[key]);
    }
  });

  return jobIds;
}

const collapseSectionProps = () => ({
  headerConfig: {
    containerStyles: () => ({
      backgroundColor: 'ats-tertiary',
      p: '15px',
    }),
    textStyles: {
      color: 'ats-primary',
    },
    iconStyles: {
      width: '24px',
      height: '24px',
    },
  },
  styles: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
  },
  collapseStyles: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1em 2em',
    backgroundColor: 'base-white',
  },
});

export const jobSectionConfig = ({
  section,
  formData,
  formRef,
  setFormData,
  moreOptions = [],
  jobId,
}) => {
  const id = formData[section]?.length + 1;
  const formJobId = `${section}_job_${id}`;
  const formStageId = `${section}_stage_${id}`;
  const customJobFormId = `${section}_customJobFormId_${id}`;
  // Live exclusion of jobs already picked in *other* rows so the same job can't
  // be added twice. Computed lazily (read by JobListMenu on open) rather than
  // frozen into the options list — the field config is created once and cached
  // in state, so a snapshot taken here would go stale the moment another row's
  // selection changes. Reading formRef live keeps it correct. (CORE-4740)
  const getExcludedJobIds = () => {
    const currentRowJobId = formRef?.current?.values?.[formJobId];
    return extractJobIds(formRef?.current?.values || {}).filter(
      (value) => value !== currentRowJobId
    );
  };
  return [
    id > 1 && {
      type: 'custom',
      name: `${section}_divider_${id}`,

      Field: () => {
        return (
          <Box p="10px 0" m="0 -30px">
            <Divider />
          </Box>
        );
      },
    },
    !jobId && {
      type: 'custom',
      name: `${section}_job_trash_${id}`,
      Field: () => {
        return (
          <Box pos={'relative'}>
            <Icon
              ml="10px"
              pos="absolute"
              right="0"
              as={Trash01}
              onClick={() => {
                setFormData((prev) => {
                  const updatedSection = prev[section].filter(
                    (item, _index) =>
                      ![
                        formJobId,
                        formStageId,
                        customJobFormId,
                        `${section}_job_trash_${id}`,
                        `${section}_divider_${id}`,
                        `${section}_automation_preview_${id}`,
                      ].includes(item?.name)
                  );

                  return {
                    ...prev,
                    [section]: updatedSection,
                  };
                });
              }}
              cursor="pointer"
            />
          </Box>
        );
      },
    },
    {
      type: 'custom',
      name: customJobFormId,
      Field: () => {
        const [_job_field, { value: formJobIdValue }] = useField(formJobId);
        const [, , { setValue }] = useField(formStageId);

        useEffect(() => {
          const fetchJobStageAndUpdateForm = async () => {
            if (!formJobIdValue) {
              // If formJobIdValue is falsy (null, undefined, empty), remove the stage field
              setFormData((prev) => {
                const updatedJobs = prev.jobs.filter(
                  (field) => field.name !== formStageId
                );
                return {
                  ...prev,
                  jobs: updatedJobs,
                };
              });
              return;
            }

            // Fetch job stages by job ID
            const jobStageOptions = {
              endPoint: `/ats_jobs/${formJobIdValue}/ats_job_stages`,
              processResponse: (res) => res?.data,
            };

            const [stagesResponse] = await Promise.all([
              fetchSelectOptions(jobStageOptions),
            ]);
            // Update form data with new options
            setFormData((prev) => {
              const existingStageFieldIndex = prev.jobs.findIndex(
                (i) => i?.name === formStageId
              );
              const newStageField = {
                name: formStageId,
                label: 'Interview Stage',
                isRequired: true,
                type: 'select',
                options: stagesResponse,
                valueKey: 'id',
                labelKey: 'name',
                // Lightning marker on stages that have automation configured.
                customFormatLabel: renderStageOptionLabel,
              };

              const updatedFields = [...prev.jobs];

              if (existingStageFieldIndex > -1) {
                // Update existing stage field
                updatedFields[existingStageFieldIndex] = newStageField;
              } else {
                // Insert new stage field next to the job field
                const jobFieldIndex = prev.jobs.findIndex(
                  (i) => i?.name === formJobId
                );
                updatedFields.splice(jobFieldIndex + 1, 0, newStageField);
              }

              return {
                ...prev,
                jobs: updatedFields,
              };
            });

            // Select the application review as default stage
            const lockedApplicationReviewStage = getFirstApplicationReviewStage(
              stagesResponse,
              ATS_MILESTONES
            );
            // Set value for the stage field
            setValue(lockedApplicationReviewStage?.id);
          };

          fetchJobStageAndUpdateForm();

          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formJobIdValue]);

        return null;
      },
    },

    {
      name: formJobId,
      label: 'Job',
      type: 'select',
      styleVersion: 'v1',
      useInput: true,
      isRequired: true,
      isMulti: false,
      disabled: jobId,
      defaultValue: moreOptions?.[0]?.value,
      placeholder: `Search jobs`,
      options: moreOptions?.map((item) => ({
        value: item?.value || item?.id,
        label: item?.label || item?.name,
        status: item?.status || 'published',
        department: item?.department,
        locations: item?.locations || [],
      })),
      // Reactive duplicate-job exclusion — JobListMenu calls this on open and
      // hides jobs currently selected in other rows (see getExcludedJobIds).
      getExcludedJobIds,
      newStyles: [
        {
          type: 'menu',
          newStyles: {
            padding: '0px !important',
            borderRadius: '6px !important',
            border: '1px solid #EDEDED',
            borderColor: 'border-color',
            boxShadow: 'none',
          },
        },
      ],
      components: {
        Menu: JobListMenu,
        IndicatorSeparator: () => null,
      },
      filterOption: () => true,
      validationType: 'array',
    },
    {
      type: 'custom',
      name: `${section}_automation_preview_${id}`,
      // Inline automation preview for the stage picked just above — shows the
      // destination stage's read-only activity/timing summary when that stage
      // has automation configured (renders nothing otherwise). Reads the live
      // stage value so it updates as the user changes the stage.
      Field: () => {
        const [, { value: stageValue }] = useField(formStageId);
        return (
          <StageAutomationPreview
            stageId={stageValue}
            dataTestId={`automation-preview-create-${id}`}
            // The drawer body already scrolls, so grow the preview inline
            // rather than giving the automation info its own inner scrollbar
            // (mirrors the ImportCandidateToJob call site).
            scrollableActivities={false}
          />
        );
      },
    },
  ].filter(Boolean);
};

const flowsSectionConfig = () => {
  return [
    {
      name: `outreach_ids`,
      label: 'Flows',
      placeholder: 'Select or Search for Flows',
      type: 'select',
      fetchConfig: {
        endPoint: `/outreaches/list`,
        processResponse: (res) => res?.data,
        httpType: 'v1',
      },
      isClearable: true,
      valueKey: 'id',
      labelKey: 'title',
    },
  ];
};

const projectsSectionConfig = ({ projectId }) => {
  return [
    {
      name: `project_ids`,
      label: 'Project',
      placeholder: 'Select or Search for projects',
      type: 'select',
      isMulti: true,
      disabled: projectId,
      fetchConfig: {
        endPoint: `/projects/list`,
        processResponse: (res) => res?.data?.projects,
        httpType: 'v1',
      },
      valueKey: 'id',
      labelKey: 'name',
    },
  ];
};

export const attributionsSectionConfig = ({ users }) => {
  return [
    {
      // Use the same hierarchical Source picker as the candidate edit drawer
      // (grouped parents, selectable leaves, selected-state checkmark) instead
      // of a plain grouped select. CandidateSourceMenu builds the tree from the
      // flat list's `parent_id`, so return the raw list (not grouped). CORE-4716.
      name: `ats_candidate_source_id`,
      label: 'Source',
      type: 'kSelect',
      isMulti: false,
      valueKey: 'id',
      labelKey: 'name',
      components: getCandidateSourceComponents(),
      styles: {
        menu: (base) => ({ ...base, padding: 0 }),
        menuList: (base) => ({ ...base, padding: 0 }),
      },
      fetchConfig: {
        endPoint: `ats_candidate_sources`,
        // Only enabled, non-deleted sources — same filter as the edit drawer.
        processResponse: (res) =>
          res?.data?.filter((item) => item?.enabled && !item?.deleted),
      },
    },
    {
      name: `credited_to_user_id`,
      label: 'Credited to',
      customFormatLabel: SelectOptionWithAvatar,
      components: {
        Option: SelectWithDescriptionOption,
      },
      extraProps: {
        showAvatar: true,
        labelKey: 'name',
        descKey: 'email',
        avatarKey: 'avatar',
        isNearLabel: true,
      },
      showAvatar: true,
      type: 'autoComplete',
      options: users,
      fetchConfig: {
        method: 'get',
        endPoint: `account/users/search?filter_by[status][]=active&filter_value=`,
        processResponse: (res) =>
          res?.data?.users?.map((item) => ({
            name: `${item?.first_name} ${item?.last_name}`,
            id: item?.id,
            avatar: item?.avatar_thumb_url,
            ...item,
          })),
        httpType: 'v1',
      },
      updateEndpointOnChange: true,
      loadDefaultOptions: false,
      useInput: true,
      valueKey: 'id',
      labelKey: 'name',
    },
    {
      // Referral toggle whose referrer mirrors the "Credited to" select above —
      // `formik` is injected by FormItem for custom fields. Only shown when
      // Source is "Employee Referral".
      type: 'custom',
      name: 'mark_as_referred',
      Field: MarkAsReferredField,
      props: {
        users,
        sourceFieldName: 'ats_candidate_source_id',
      },
    },
  ];
};

const checkLinkedInMatch = async (
  linkedinUrl,
  formRef,
  setDeleted,
  setDeletedLinkedinValues
) => {
  const formValues = formRef?.current?.values;
  const currentFirstName = formValues?.first_name || '';
  const currentLastName = formValues?.last_name || '';

  if (linkedinUrl) {
    try {
      const method = 'get';
      const path = `contacts/matching?first_name=${currentFirstName}&last_name=${currentLastName}&linkedin=`;
      const endPoint = `${path}${encodeURIComponent(linkedinUrl)}`;

      const response = await fetchSelectOptions({
        method: method,
        endPoint,
      });
      setDeleted({ ...response?.contact });
      const linkedinData = response?.contact || {};
      setDeletedLinkedinValues(linkedinData);
    } catch (error) {
      console.error('Error fetching LinkedIn data:', error);
    }
  }
};

type DeletedLinkedinValues = {
  deleted?: boolean;
  deleted_reason?: string;
  deleted_by?: string;
};

const socialsSectionConfig = ({
  section,
  formData,
  formRef,
  setFormData,
  setDeleted,
}) => {
  const id = formData[section]?.length + 1;
  const formKindId = `${section}_kind_${id}`;
  const formUrlId = `${section}_url_${id}`;
  const trashId = `${section}_trash_${id}`;
  const divId = `${section}_divider_${id}`;
  const kinds = extractSocials(formRef?.current?.values || {});
  let deletedLinkedinValues: DeletedLinkedinValues = {};

  const socialOptions = CONTACT_SOCIALS?.filter(
    (item) => !kinds.includes(item.value)
  );

  // Common function for LinkedIn matching API call

  return [
    id > 1 && {
      type: 'custom',
      name: divId,
      Field: () => {
        return (
          <Box p="10px 0" m="0 -30px">
            <Divider />
          </Box>
        );
      },
    },
    {
      type: 'custom',
      name: trashId,
      Field: () => {
        return (
          <Box pos={'relative'}>
            <Icon
              ml="10px"
              pos="absolute"
              right="0"
              as={Trash01}
              onClick={() => {
                setFormData((prev) => {
                  const updatedSection = prev[section].filter(
                    (item) =>
                      ![formKindId, formUrlId, trashId, divId].includes(
                        item?.name
                      )
                  );

                  return {
                    ...prev,
                    [section]: updatedSection,
                  };
                });
              }}
              cursor="pointer"
            />
          </Box>
        );
      },
    },
    {
      name: formKindId,
      label: 'Type',
      type: 'select',
      isRequired: true,
      options: socialOptions,
      customOnChange: async (selectedType) => {
        if (selectedType?.value === 'linkedin') {
          const formValues = formRef?.current?.values;
          const urlFieldName = formKindId.replace('_kind_', '_url_');
          const currentUrl = formValues?.[urlFieldName];
          if (currentUrl && REGEX_PATTERNS.linkedin.test(currentUrl)) {
            await checkLinkedInMatch(
              currentUrl,
              formRef,
              setDeleted,
              (data) => {
                deletedLinkedinValues = data;
              }
            );
          }
        } else {
          deletedLinkedinValues = {};
        }
      },
    },
    {
      name: formUrlId,
      label: 'URL',
      isRequired: true,
      type: 'input',
      maxLength: 255,
      onPaste: (e, setValue) => {
        const kindFieldName = formUrlId.replace('_url_', '_kind_');
        const selectedType = formRef?.current?.values?.[kindFieldName];
        if (selectedType === 'linkedin') handleLinkedInUrlPaste(e, setValue);
      },
      debounceOnType: async (value) => {
        const formValues = formRef?.current?.values;
        const kindFieldName = formUrlId.replace('_url_', '_kind_');
        const selectedType = formValues?.[kindFieldName];
        if (
          selectedType === 'linkedin' &&
          value &&
          REGEX_PATTERNS.linkedin.test(value)
        ) {
          await checkLinkedInMatch(value, formRef, setDeleted, (data) => {
            deletedLinkedinValues = data;
          });
        }
      },
      validation: Yup.string()
        .required('URL is required')
        .test('url-validation', function (value) {
          const url = value?.trim();
          if (!url) return true;

          const formValues = this.parent;
          const kindFieldName = formUrlId.replace('_url_', '_kind_');
          const selectedType = formValues?.[kindFieldName];
          const pattern = REGEX_PATTERNS[selectedType];

          const label = selectedType
            ? selectedType.charAt(0).toUpperCase() + selectedType.slice(1)
            : (formUrlId?.split('_url_')?.[0]?.replace(/_/g, ' ') ??
              'social media');

          if (pattern) {
            if (!pattern.test(url)) {
              return this.createError({
                message: `Please enter a valid ${label} URL`,
              });
            }
            return true;
          }

          if (!REGEX_PATTERNS.genericUrl.test(url)) {
            return this.createError({
              message: `Please enter a valid ${label} URL`,
            });
          }

          return true;
        }),
    },
    {
      type: 'custom',
      name: 'delete_banner_for_contact',
      Field: () => {
        if (
          !deletedLinkedinValues ||
          Object.keys(deletedLinkedinValues).length === 0 ||
          !deletedLinkedinValues?.deleted
        )
          return null;
        return (
          <Box mb="12px">
            <UnDeleteCandidateBanner
              reason={deletedLinkedinValues?.deleted_reason}
              deleted_by={deletedLinkedinValues?.deleted_by}
            />
          </Box>
        );
      },
    },
  ].filter(Boolean);
};

const educationSectionConfig = ({
  section,
  formData,
  formRef: _formRef,
  setFormData,
  getDegree,
}) => {
  const id = formData[section]?.length + 1;
  const degreeId = `${section}_degree_${id}`;
  const institution = `${section}_institution_${id}`;
  const startDate = `${section}_startDate_${id}`;
  const endDate = `${section}_endDate_${id}`;
  const endDateDisabled = `${section}_endDateDisabled_${id}`;
  const trashId = `${section}_trash_${id}`;
  const divId = `${section}_divider_${id}`;
  const isPresent = `${section}_isPresent_${id}`;
  const dateValidation = `${section}_dateValidation_${id}`;

  return [
    {
      type: 'custom',
      name: divId,
      Field: () => {
        return (
          <Box p="10px 0" m="0 -30px">
            <Divider />
          </Box>
        );
      },
    },
    {
      type: 'custom',
      name: trashId,
      Field: () => {
        return (
          <Box pos={'relative'}>
            <Icon
              ml="10px"
              pos="absolute"
              right="0"
              as={Trash01}
              onClick={() => {
                setFormData((prev) => {
                  const updatedSection = prev[section].filter(
                    (item) =>
                      ![
                        degreeId,
                        institution,
                        startDate,
                        endDate,
                        endDateDisabled,
                        trashId,
                        divId,
                        isPresent,
                        dateValidation,
                      ].includes(item?.name)
                  );

                  return {
                    ...prev,
                    [section]: updatedSection,
                  };
                });
              }}
              cursor="pointer"
            />
          </Box>
        );
      },
    },
    {
      name: institution,
      label: 'School',
      type: 'asyncSelectWithCreate',
      useInput: true,
      placeholder: 'Add school here',
      options: [],
      isRequired: false,
      fetchConfig: {
        endPoint: `/academics/institutions?q=`,
        processResponse: (res) =>
          res?.data?.map((opt) => ({
            ...opt,
            label: opt.name,
            value: opt.id,
          })),
      },
      isMulti: false,
      createPlaceHolder: 'Create as new school',
    },
    {
      name: degreeId,
      label: 'Degree',
      type: 'select',
      isRequired: false,
      placeholder: 'Add degree here',
      labelKey: 'name',
      options: getDegree,
      valueKey: 'id',
      isMulti: false,
    },
    {
      name: startDate,
      label: 'Start Date',
      type: 'monthYear',
    },
    {
      name: endDate,
      label: 'End Date',
      type: 'monthYear',
      isConditional: true,
      conditionConfig: {
        field: isPresent,
        type: 'custom',
        customFunc: (value) => {
          return value !== true;
        },
      },
      fieldValidate: ({ formik }) => {
        const { values } = formik || {};

        const startDateValue = moment(values?.[startDate]);
        const endDateValue = moment(values?.[endDate]);

        if (
          startDateValue?.isValid?.() &&
          endDateValue?.isValid?.() &&
          endDateValue?.isBefore?.(startDateValue)
        ) {
          return 'Invalid education. End date must be after start date.';
        }
      },
    },
    {
      name: dateValidation,
      type: 'custom',
      Field: () => {
        const [, { value: startDateValue }, startHelpers] = useField(startDate);
        const { setError: setStartError } = startHelpers;
        const [, { value: endDateValue }, endHelpers] = useField(endDate);
        const { setError: setEndError } = endHelpers;

        useEffect(() => {
          const validateDates = () => {
            let startDate, endDate;

            // Clear previous errors
            setStartError(undefined);
            setEndError(undefined);

            // Validate start date if present
            if (startDateValue) {
              startDate = moment(startDateValue);
              if (!startDate.isValid()) {
                setStartError('Invalid start date format');
                return;
              }
            }

            // Validate end date if present
            if (endDateValue) {
              endDate = moment(endDateValue);
              if (!endDate.isValid()) {
                setEndError('Invalid end date format');
                return;
              }
            }

            // If both dates are present, ensure end date is after start date
            if (startDate && endDate && endDate.isSameOrBefore(startDate)) {
              setEndError(
                'Invalid education. End date must be after start date.'
              );
            }
          };

          validateDates();
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [startDateValue, endDateValue]);

        return null;
      },
    },
    {
      name: endDateDisabled,
      label: 'End Date',
      type: 'input',
      isRequired: false,
      disabled: true,
      isConditional: true,
      conditionConfig: { field: isPresent, value: true },
      style: { opacity: '1 !important', background: '#F9F9FB' },
    },
    {
      name: isPresent,
      label: 'Present',
      type: 'checkbox',
    },
  ].filter(Boolean);
};

const experienceSectionConfig = ({ section, formData, setFormData }) => {
  const id = formData[section]?.length + 1;
  const title = `${section}_title_${id}`;
  const company = `${section}_company_${id}`;
  const startDate = `${section}_startDate_${id}`;
  const endDate = `${section}_endDate_${id}`;
  const endDateDisabled = `${section}_endDateDisabled_${id}`;
  const trashId = `${section}_trash_${id}`;
  const divId = `${section}_divider_${id}`;
  const isPresent = `${section}_isPresent_${id}`;
  const dateValidation = `${section}_dateValidation_${id}`;

  return [
    {
      type: 'custom',
      name: divId,
      Field: () => {
        return (
          <Box p="10px 0" m="0 -30px">
            <Divider />
          </Box>
        );
      },
    },
    {
      type: 'custom',
      name: trashId,
      Field: () => {
        return (
          <Box pos={'relative'}>
            <Icon
              ml="10px"
              pos="absolute"
              right="0"
              as={Trash01}
              onClick={() => {
                setFormData((prev) => {
                  const updatedSection = prev[section].filter(
                    (item) =>
                      ![
                        title,
                        company,
                        startDate,
                        endDate,
                        endDateDisabled,
                        trashId,
                        divId,
                        isPresent,
                        dateValidation,
                      ].includes(item?.name)
                  );

                  return {
                    ...prev,
                    [section]: updatedSection,
                  };
                });
              }}
              cursor="pointer"
            />
          </Box>
        );
      },
    },
    {
      name: title,
      label: 'Title',
      isRequired: false,
      type: 'input',
      maxLength: 255,
    },
    {
      name: company,
      label: 'Company',
      type: 'asyncSelectWithCreate',
      useInput: true,
      placeholder: 'Add company here',
      options: [],
      isRequired: false,
      components: {
        Option: ({ children, data, ...props }) => {
          return (
            <components.Option {...props}>
              <Flex alignItems="center">
                <Avatar
                  name={data.name}
                  size="sm"
                  src={`http://${data.logo}`}
                />
                <Box
                  as="span"
                  display="inline-block"
                  ml="sm"
                  textTransform="capitalize"
                >
                  {children}
                </Box>
              </Flex>
            </components.Option>
          );
        },
      },
      fetchConfig: {
        endPoint: `/search/circles/autocomplete?filter_name=company&filter_value=`,
        processResponse: (res) =>
          res?.data?.autocomplete.map((opt) => ({
            ...opt,
            label: opt.name,
            value: opt.id,
          })),
        httpType: 'v1',
      },
      isMulti: false,
      createPlaceHolder: 'Create as new company',
    },
    {
      name: startDate,
      label: 'Start Date',
      type: 'monthYear',
    },
    {
      name: endDate,
      label: 'End Date',
      type: 'monthYear',
      isConditional: true,
      conditionConfig: {
        field: isPresent,
        type: 'custom',
        customFunc: (value) => {
          return value !== true;
        },
      },
      fieldValidate: ({ formik }) => {
        const { values } = formik || {};

        if (!values?.[startDate] || !values?.[endDate]) {
          return;
        }
        const startDateValue = moment(values?.[startDate]);
        const endDateValue = moment(values?.[endDate]);

        if (
          startDateValue?.isValid?.() &&
          endDateValue?.isValid?.() &&
          endDateValue?.isBefore?.(startDateValue)
        ) {
          return 'Invalid experience. End date must be after start date.';
        }
      },
    },
    {
      name: dateValidation,
      type: 'custom',
      Field: () => {
        const [, { value: startDateValue }, startHelpers] = useField(startDate);
        const { setError: setStartError } = startHelpers;
        const [, { value: endDateValue }, endHelpers] = useField(endDate);
        const { setError: setEndError } = endHelpers;

        useEffect(() => {
          const validateDates = () => {
            let startDate, endDate;

            // Clear previous errors
            setStartError(undefined);
            setEndError(undefined);

            // Validate start date if present
            if (startDateValue) {
              startDate = moment(startDateValue);
              if (!startDate.isValid()) {
                setStartError('Invalid start date format');
                return;
              }
            }

            // Validate end date if present
            if (endDateValue) {
              endDate = moment(endDateValue);
              if (!endDate.isValid()) {
                setEndError('Invalid end date format');
                return;
              }
            }

            // If both dates are present, ensure end date is after start date
            if (startDate && endDate && endDate?.isBefore?.(startDate)) {
              setEndError(
                'Invalid experience. End date must be after start date.'
              );
            }
          };

          validateDates();
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [startDateValue, endDateValue]);

        return null;
      },
    },
    {
      name: endDateDisabled,
      label: 'End Date',
      type: 'input',
      disabled: true,
      isConditional: true,
      conditionConfig: { field: isPresent, value: true },
      style: { opacity: '1 !important', background: '#F9F9FB' },
    },
    {
      name: isPresent,
      label: 'Present',
      type: 'checkbox',
    },
  ].filter(Boolean);
};

const profileSectionConfig = ({
  Country,
  customFields,
  firstName,
  lastName,
  setDeleted,
  isDeletedContact,
  setDuplicateContact,
  duplicateContact,
  hasLinkedInUrl,
  defaultPhoneCountry,
  // formRef,
}) => [
  {
    id: 'personalInfo',
    type: 'wrapper',
    fields: [
      {
        name: 'first_name',
        label: 'First name',
        type: 'input',
        maxLength: 255,
        isRequired: true,
      },
      {
        name: 'last_name',
        label: 'Last name',
        maxLength: 255,
        type: 'input',
      },
      {
        name: 'email',
        label: 'Email',
        type: 'input',
        inputType: 'email',
        maxLength: 255,
        isRequired: !hasLinkedInUrl,
        optionalValidation: true,
        validation: Yup.string()
          .nullable()
          .test(
            'email-or-linkedin',
            'Email or LinkedIn URL is required',
            function (value) {
              const formValues = this.parent;

              // Check if a valid LinkedIn URL exists in the socials section
              if (hasValidLinkedInUrl(formValues)) {
                return true;
              }

              // If no LinkedIn URL, email is required
              if (!value || !value.trim()) {
                return false; // Will show "Email or LinkedIn URL is required"
              }

              return true;
            }
          )
          .test(
            'email-format',
            'Valid email address required',
            function (value) {
              // Only validate format if value is provided
              if (!value || !value.trim()) {
                return true; // Let the above test handle empty case
              }
              return isValidEmail(value);
            }
          ),

        debounceOnType: async (value) => {
          // Only check once the email is a valid address
          if (!isValidEmail(value)) {
            setDeleted(false);
            setDuplicateContact?.(false);
            return;
          }

          const contact = await checkContactMatching(
            firstName,
            lastName,
            value
          );
          if (contact?.deleted) {
            setDeleted({
              ...contact,
              deletedBannerForEmailMatch: true,
            });
            setDuplicateContact?.(false);
          } else if (contact?.id) {
            // An active candidate already uses this email
            setDuplicateContact?.(contact);
            setDeleted(false);
          } else {
            // Clear both states if no matching contact is found
            setDeleted(false);
            setDuplicateContact?.(false);
          }
        },
      },
      {
        type: 'custom',
        name: 'duplicate_email_warning',
        Field: () => {
          if (!duplicateContact?.id) return null;
          return (
            <Flex mt="-8px" mb="12px" gap="4px" alignItems="center">
              <Text size="xs" color="error-200">
                Candidate already exists.
              </Text>
              <Link
                href={`/candidates/${duplicateContact.id}`}
                isExternal
                color="error-200"
                fontSize="xs"
                fontWeight="semiBold"
                textDecoration="underline"
              >
                View profile
              </Link>
            </Flex>
          );
        },
      },
      {
        type: 'custom',
        name: 'delete_banner_for_contact',
        Field: () => {
          if (
            !isDeletedContact?.deleted ||
            !isDeletedContact?.deletedBannerForEmailMatch
          )
            return null;
          return (
            <UnDeleteCandidateBanner
              reason={isDeletedContact?.deleted_reason}
              deleted_by={isDeletedContact?.deleted_by}
            />
          );
        },
      },
      {
        name: 'phone_number',
        label: 'Phone',
        type: 'phone',
        placeholder: 'Add phone number',
        defaultCountry: defaultPhoneCountry,
        inputSize: 'md',
        optionalValidation: true,
        countrySelectorTriggerClassName: 'w-[100px]',
        syncTimezone: true,
      },
      {
        type: 'custom',
        name: 'time_zone',
        Field: ({ formik }) => {
          return (
            <TimeZoneSelectorV1
              value={formik.values.time_zone}
              onChange={(value) => {
                formik.setFieldValue('time_zone', value.key);
              }}
              inputProps={{
                size: 'sm-semi-bold',
                menuPlacement: 'auto',
                menuPortalTarget: document.body,
                customStyles: {
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
              }}
            />
          );
        },
      },
      {
        name: 'location',
        label: 'Location',
        type: 'autoComplete',
        styleVersion: 'v1',
        menuPlacement: 'auto',
        validationType: 'object',
        labelKey: 'full_address',
        useInput: true,
        initialSearchQuery: Country,
        placeholder: `Search a location`,
        updateEndpointOnChange: true,
        fetchConfig: {
          method: 'get',
          endPoint: `/ats_offices/search_location?q=`,
          processResponse: (res) =>
            res?.data?.map((item) => ({
              label: item?.full_address,
              value: item,
              ...item,
            })),
        },
      },
      {
        name: 'tags',
        label: 'Tags',
        type: 'asyncSelectWithCreate',
        menuPlacement: 'auto',
        useInput: true,
        placeholder: 'Add tags here',
        isMulti: true,
        fetchConfig: {
          endPoint: `/search/circles/autocomplete_tags?filter_name=tags&filter_value=`,
          processResponse: (res) =>
            res?.data?.autocomplete.map((opt) => ({
              ...opt,
              label: opt.value,
              value: opt.value,
            })),
          httpType: 'v1',
        },
        createPlaceHolder: 'Create as new tag and add',
        containerStyles: {},
      },
      {
        name: 'skills',
        label: 'Skills',
        type: 'asyncSelectWithCreate',
        useInput: true,
        placeholder: 'Add skills',
        isMulti: true,
        fetchConfig: {
          endPoint: `/search/circles/autocomplete_skills?filter_name=skills&include_global=true&filter_value=`,
          processResponse: (res) =>
            res?.data?.autocomplete.map((opt) => ({
              ...opt,
              label: opt.value,
              value: opt.value,
            })),
          httpType: 'v1',
        },
        createPlaceHolder: 'Create as new skill and add',
        containerStyles: {},
      },
      ...customFields,
    ],

    Wrapper: CollapseSection,
    wrapperProps: {
      title: 'PERSONAL INFO',
      isHeaderClickable: true,
      defaultIsOpen: true,
      ...collapseSectionProps(),
    },
  },
];

type ResumeConfig = {
  generation_id?: string;
};

type ResumeDataType = {
  config?: ResumeConfig;
  resume?: {
    Education?: any;
    Experience?: any;
    LinkedInURL?: string;
    GithubURL?: string;
    Country?: string;
  };
};

export const useCreateContactConfig = ({
  parsedResume = {} as ResumeDataType,
  getDegree,
  formRef,
  defaults = [],
  setDeleted,
  isDeletedContact,
  setDuplicateContact,
  duplicateContact,
  hasLinkedInUrl = false,
}) => {
  const [formData, setFormData] = useState({
    profile: {},
    jobs: [],
    flows: [],
    projects: [],
    attributions: [],
    socials: [],
    education: [],
    experience: [],
  });

  const [resumeData, setResume] = useState<ResumeDataType>(parsedResume);
  const { jobId, projectId } = useParams();
  const { data: atsJobs = [], isLoading: isAtsJobsLoading } =
    useGetAllJobsListQuery({
      params: {
        scope: 'viewable',
        filter_by: { job_statuses: ['published'] },
      },
    });

  const { currentJob = {} } = useSelector(getAtsJobCreate);

  const { data: { name = '' } = {} } = currentJob;

  const [customForm, setCustomForm] = useState([]);

  const {
    data: { ats_form_fields = [] } = {},
    isLoading: isCustomFieldsLoading,
  } = useQuery({
    queryKey: ['contacts/custom_fields', 'contact'],
    queryFn: async () => {
      const response = await fetchSelectOptions({
        endPoint: 'ats_custom_fields/form_fields?subject_type=contact',
        method: 'get',
      });
      return response;
    },
    staleTime: 0,
  });

  useEffect(() => {
    if (ats_form_fields?.length > 0) {
      const res = generateCustomFormConfig(
        {
          ats_custom_fields: ats_form_fields,
        },
        true
      );
      const custom = res?.custom ?? [];
      setCustomForm([...custom]);
    }
  }, [ats_form_fields]);

  useEffect(() => {
    setResume((prev) => {
      return prev?.config?.generation_id !==
        parsedResume?.config?.generation_id ||
        (prev?.config?.generation_id === undefined &&
          parsedResume?.config?.generation_id)
        ? parsedResume
        : prev;
    });
  }, [parsedResume]);

  const { Education, Experience, LinkedInURL, GithubURL, Country } =
    resumeData?.resume || {};

  useEffect(() => {
    if (jobId) {
      const jobOptions = { value: parseInt(jobId), label: name };
      defaults?.map((item) => addSection(item, [jobOptions]));
    } else {
      defaults?.map((item) => addSection(item, atsJobs));
    }
    addSection('attributions');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      socials: [],
      education: [],
      experience: [],
    }));
    const addSections = () => {
      const urlRegex =
        /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

      if (LinkedInURL && REGEX_PATTERNS.linkedin.test(LinkedInURL))
        addSection('socials');
      if (GithubURL?.length > 0 && urlRegex.test(GithubURL[0]))
        addSection('socials');
      if (Education?.length > 0)
        Education.forEach((i, ind) =>
          setTimeout(
            () => {
              addSection('education');
            },
            (ind + 1) * 500
          )
        );
      if (Experience?.length > 0)
        Experience.forEach((i, ind) =>
          setTimeout(
            () => {
              addSection('experience');
            },
            (ind + 1) * 500
          )
        );
    };

    addSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeData]);

  const customFields = [...customForm];

  const users = useSelector(selectUsers);
  const currUser = useSelector(selectCurrUser);

  const hasResume = Boolean(parsedResume?.resume);
  const defaultPhoneCountry = hasResume
    ? undefined
    : getCountryFromTimezone(currUser?.time_zone) ||
      getCountryFromTimezone(currUser?.account?.time_zone) ||
      undefined;

  const fieldsConfig = {
    jobs: jobSectionConfig,
    flows: flowsSectionConfig,
    projects: projectsSectionConfig,
    attributions: attributionsSectionConfig,
    socials: socialsSectionConfig,
    education: educationSectionConfig,
    experience: experienceSectionConfig,
  };

  const addSection = (section, moreOptions = []) => {
    setFormData((prev) => {
      // Attribution is a singleton section (no "Add" button) — never append it
      // more than once, even if the init effect runs twice (e.g. StrictMode or
      // when it's also listed in `defaults`).
      if (section === 'attributions' && prev.attributions?.length > 0) {
        return prev;
      }

      const newSectionFields = fieldsConfig[section]?.({
        section,
        formData: prev,
        formRef,
        setFormData,
        users,
        moreOptions,
        jobId,
        projectId,
        getDegree,
        setDeleted,
      });

      return {
        ...prev,
        [section]: [...prev[section], ...newSectionFields],
      };
    });
  };

  const generateSectionConfig = (section, sectionName, moreOptions = []) => {
    // Each social link contains 4 fields.
    // The last social link does not require a bottom divider, so we subtract 1 from the total field count.
    const MAX_SOCIAL_LINKS_FIELDS = CONTACT_SOCIALS?.length * 4 - 1;

    const shouldShowActionButton = () => {
      if (section === 'socials') {
        return formData.socials.length < MAX_SOCIAL_LINKS_FIELDS;
      }
      return !['flows', 'projects', 'attributions', jobId && 'jobs'].includes(
        section
      );
    };

    const actionButton = {
      type: 'custom',
      Field: () => {
        return (
          <IconButton
            icon={FaPlus}
            text={`Add ${sectionName}`}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              addSection(section, moreOptions);
            }}
          />
        );
      },
    };

    return [
      {
        id: section,
        type: 'wrapper',
        Wrapper: CollapseSection,
        wrapperProps: {
          title: sectionName,
          isHeaderClickable: true,
          defaultIsOpen: true,
          ...collapseSectionProps(),
        },
        fields:
          formData?.[section]?.length === 0
            ? [actionButton]
            : [
                ...(formData?.[section] || []),
                shouldShowActionButton() && actionButton,
              ].filter(Boolean),
      },
    ];
  };

  const firstName = formRef?.current?.values?.first_name?.toLowerCase();
  const lastName = formRef?.current?.values?.last_name?.toLowerCase();

  const globalFormConfig = [
    ...profileSectionConfig({
      Country,
      customFields,
      firstName,
      lastName,
      setDeleted,
      isDeletedContact,
      setDuplicateContact,
      duplicateContact,
      hasLinkedInUrl,
      defaultPhoneCountry,
      // formRef,
    }),
    ...generateSectionConfig('jobs', 'Job', atsJobs),
    ...(!jobId
      ? [
          ...generateSectionConfig('flows', 'Flow'),
          ...generateSectionConfig('projects', 'Project'),
        ]
      : []),
    ...generateSectionConfig('attributions', 'Attribution'),
    ...generateSectionConfig('socials', 'Social Link'),
    ...generateSectionConfig('education', 'Education'),
    ...generateSectionConfig('experience', 'Experience'),
  ];

  return {
    formData,
    globalFormConfig,
    isLoading: isAtsJobsLoading || isCustomFieldsLoading,
  };
};
